import { useContext, useMemo } from 'react'
import Box from '@/components/common/Box'
import { spacing } from '@/styles/designTokens'
import RefContext from '@/contexts/RefContext'
import { useMenuCategories } from '@/pages/menu/hooks/useMenuCategories'
import { useMenuItems } from '@/pages/menu/hooks/useMenuItems'
import type { MenuItem as MenuItemData } from '@/shared/api/menu'
import MenuItem from '@/components/menu/MenuItem'
import EmptySearchIllustration from '@/components/menu/EmptySearchIllustration'
import MenuSkeleton from '@/components/menu/MenuSkeleton'
import { useFormatMessage } from '@/translations/useFormatMessage'
import {
	CategoryHeadingRow,
	CategoryName,
	CategoryResultCount,
	CategorySection,
	ClearSearchButton,
	EmptySearchHint,
	EmptySearchState,
	EmptySearchTitle,
	ItemGrid,
	ItemSlot,
	SearchResultsHeader,
	SearchResultsTitle,
} from './MenuPage.styled'

interface MenuPageProps {
	scheduleId?: string
	searchTerm?: string
	onClearSearch?: () => void
	currency?: string
}

export default function MenuPage({
	scheduleId = '',
	searchTerm = '',
	onClearSearch = () => {},
	currency = 'USD',
}: MenuPageProps) {
	const locationRef = useContext(RefContext) ?? ''
	const { t } = useFormatMessage()

	const { data: categoriesData } = useMenuCategories(locationRef, scheduleId)
	const categories = useMemo(() => categoriesData?.categories ?? [], [categoriesData?.categories])

	const { data: itemsData, isLoading: itemsLoading } = useMenuItems(locationRef, scheduleId)
	const isLoading = itemsLoading

	const itemsByRef = useMemo(() => {
		const map = new Map<string, MenuItemData>()
		for (const item of itemsData?.items ?? []) {
			map.set(item.ref, item)
		}
		return map
	}, [itemsData?.items])

	const filteredCategories = useMemo(() => {
		if (!searchTerm) return categories
		return categories
			.map((category) => ({
				...category,
				items: category.items.filter((itemRef) => {
					const item = itemsByRef.get(itemRef)
					return item && item.name.toLowerCase().includes(searchTerm.toLowerCase())
				}),
			}))
			.filter((category) => category.items.length > 0)
	}, [categories, itemsByRef, searchTerm])

	const isSearching = searchTerm.length > 0
	const hasResults = filteredCategories.length > 0

	if (isLoading) return <MenuSkeleton />

	return (
		<Box sx={{ pb: `${spacing.pageBottomPadding}px` }}>
			{isSearching && (
				<SearchResultsHeader>
					<SearchResultsTitle>
						{t('page:menu:searchResults:header', { searchTerm })}
					</SearchResultsTitle>
					<ClearSearchButton type="button" onClick={onClearSearch}>
						{t('page:menu:clearSearch:button')}
					</ClearSearchButton>
				</SearchResultsHeader>
			)}

			{isSearching && !hasResults && (
				<EmptySearchState>
					<EmptySearchIllustration />
					<EmptySearchTitle>{t('page:menu:searchEmpty:title')}</EmptySearchTitle>
					<EmptySearchHint>{t('page:menu:searchEmpty:hint')}</EmptySearchHint>
				</EmptySearchState>
			)}

			{filteredCategories.map((category) => {
				const resolvedItems = category.items
					.map((itemRef) => itemsByRef.get(itemRef))
					.filter((item): item is MenuItemData => item !== undefined)

				// Sold-out items sorted to the bottom of each category
				const sortedItems = [
					...resolvedItems.filter((i) => i.stockStatus !== 'OUT_OF_STOCK'),
					...resolvedItems.filter((i) => i.stockStatus === 'OUT_OF_STOCK'),
				]

				if (sortedItems.length === 0) return null

				return (
					<CategorySection id={`category-${category.ref}`} key={category.ref}>
						<CategoryHeadingRow>
							<CategoryName>{category.name}</CategoryName>
							{isSearching && (
								<CategoryResultCount>
									{t('page:menu:searchResultsCount', {
										count: sortedItems.length,
									})}
								</CategoryResultCount>
							)}
						</CategoryHeadingRow>

						<ItemGrid role="list">
							{sortedItems.map((item) => (
								<ItemSlot key={item.ref} role="listitem">
									<MenuItem item={item} currency={currency} />
								</ItemSlot>
							))}
						</ItemGrid>
					</CategorySection>
				)
			})}
		</Box>
	)
}
